// src/components/tasks/RecurringTasksView.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../contexts/authClient'; // ✅ instance partagée avec intercepteur CSRF
import {
  Calendar, Clock, Plus, Edit2, Trash2, Eye, X,
  Search, RefreshCw, Loader, Download, FileText, FileSpreadsheet,
  Repeat, CheckCircle, AlertCircle,
  ChevronDown, ChevronRight, Play, Pause
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Stat Card Component ──────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color, sub }) => (
  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
        <p className="text-2xl font-bold text-white mt-1" style={{ color }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </div>
      <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
        <Icon size={18} style={{ color }} />
      </div>
    </div>
  </div>
);

// ─── Frequency Badge ──────────────────────────────────────────────────────────
const FrequencyBadge = ({ frequency }) => {
  const config = {
    daily: { icon: Clock, color: 'text-blue-400 bg-blue-500/20', label: 'Daily' },
    weekly: { icon: Calendar, color: 'text-cyan-400 bg-cyan-500/20', label: 'Weekly' },
    monthly: { icon: Calendar, color: 'text-purple-400 bg-purple-500/20', label: 'Monthly' },
    quarterly: { icon: Calendar, color: 'text-indigo-400 bg-indigo-500/20', label: 'Quarterly' },
    semester: { icon: Calendar, color: 'text-violet-400 bg-violet-500/20', label: 'Semester' },
    yearly: { icon: Calendar, color: 'text-emerald-400 bg-emerald-500/20', label: 'Yearly' },
    once: { icon: CheckCircle, color: 'text-gray-400 bg-gray-500/20', label: 'Once' }
  };
  const c = config[frequency] || config.once;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium ${c.color}`}>
      <Icon size={12} /> {c.label}
    </span>
  );
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ isActive }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
    isActive ? 'text-green-400 bg-green-500/20' : 'text-gray-400 bg-gray-500/20'
  }`}>
    {isActive ? <Play size={10} /> : <Pause size={10} />}
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

// ─── Delete Modal ────────────────────────────────────────────────────────────
const DeleteModal = ({ task, onConfirm, onClose }) => {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-white font-semibold">Delete Task: {task?.task_number}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-gray-300 text-sm">
            Are you sure you want to delete <span className="text-white font-medium">{task?.task_title}</span>?
            This will also cancel all pending assignments.
          </p>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Reason for deletion *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              placeholder="Explain why this recurring task is being deleted..."
              required
            />
          </div>
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={!reason}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg text-white text-sm"
          >
            Delete Task
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Task Form Modal ─────────────────────────────────────────────────────────
const TaskFormModal = ({ task, onSave, onClose, zones, templates, staff }) => {
  const [formData, setFormData] = useState({
    task_title: task?.task_title || '',
    task_description: task?.task_description || '',
    frequency: task?.frequency || 'weekly',
    interval_count: task?.interval_count || 1,
    day_of_week: task?.day_of_week || 0,
    day_of_month: task?.day_of_month || 1,
    month_of_year: task?.month_of_year || 1,
    zone_id: task?.zone_id || '',
    inspection_template_id: task?.inspection_template_id || '',
    start_date: task?.start_date || new Date().toISOString().slice(0, 10),
    end_date: task?.end_date || '',
    assigned_to: task?.assigned_to || '',
    assigned_role: task?.assigned_role || '',
    escalation_hours: task?.escalation_hours || 2,
    is_active: task?.is_active !== undefined ? task.is_active : true
  });

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = () => {
    if (!formData.task_title || !formData.start_date) {
      alert('Task title and start date are required');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800">
          <h3 className="text-white font-semibold">
            {task ? `Edit Task: ${task.task_number}` : 'Create Recurring Task'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-gray-400 text-xs block mb-1">Task Title *</label>
            <input
              type="text"
              value={formData.task_title}
              onChange={(e) => updateField('task_title', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              placeholder="e.g., Weekly Runway Inspection"
            />
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Description</label>
            <textarea
              value={formData.task_description}
              onChange={(e) => updateField('task_description', e.target.value)}
              rows={2}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              placeholder="Detailed description of the task..."
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Frequency</label>
              <select
                value={formData.frequency}
                onChange={(e) => updateField('frequency', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="semester">Semester (6 months)</option>
                <option value="yearly">Yearly</option>
                <option value="once">Once</option>
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">Interval (every X)</label>
              <input
                type="number"
                min="1"
                max="12"
                value={formData.interval_count}
                onChange={(e) => updateField('interval_count', parseInt(e.target.value))}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
          </div>
          
          {formData.frequency === 'weekly' && (
            <div>
              <label className="text-gray-400 text-xs block mb-1">Day of Week</label>
              <div className="flex gap-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => updateField('day_of_week', idx)}
                    className={`px-3 py-1.5 rounded-lg text-xs transition ${
                      formData.day_of_week === idx
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Start Date *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => updateField('start_date', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">End Date (optional)</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => updateField('end_date', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Zone (optional)</label>
              <select
                value={formData.zone_id}
                onChange={(e) => updateField('zone_id', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">— None —</option>
                {zones.map(z => (
                  <option key={z.id} value={z.id}>{z.zone_code} - {z.zone_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">Inspection Template</label>
              <select
                value={formData.inspection_template_id}
                onChange={(e) => updateField('inspection_template_id', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">— None —</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.template_code} - {t.template_name}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Assign To (Staff)</label>
              <select
                value={formData.assigned_to}
                onChange={(e) => updateField('assigned_to', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">— Unassigned —</option>
                {staff.map(s => (
                  <option key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.role})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">Or Assign by Role</label>
              <input
                type="text"
                value={formData.assigned_role}
                onChange={(e) => updateField('assigned_role', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                placeholder="e.g., fuel_handler, line_tech"
              />
            </div>
          </div>
          
          <div>
            <label className="text-gray-400 text-xs block mb-1">Escalation (hours overdue before notification)</label>
            <input
              type="number"
              min="1"
              max="72"
              value={formData.escalation_hours}
              onChange={(e) => updateField('escalation_hours', parseInt(e.target.value))}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="w-4 h-4 accent-green-500"
            />
            <label htmlFor="is_active" className="text-gray-300 text-sm">Active (auto-generate assignments)</label>
          </div>
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
            Cancel
          </button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm">
            {task ? 'Update Task' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Assignments Modal ────────────────────────────────────────────────────────
const AssignmentsModal = ({ task, onClose }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const response = await api.get(`/tasks/recurring/${task.id}`); // ✅ CSRF actif
        setAssignments(response.data.assignments || []);
      } catch (error) {
        console.error('Error fetching assignments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAssignments();
  }, [task.id]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-3xl max-h-[80vh] overflow-hidden border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <div>
            <h3 className="text-white font-semibold">Task Assignments: {task.task_number}</h3>
            <p className="text-gray-400 text-xs">{task.task_title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="text-center py-8"><Loader className="animate-spin mx-auto" /></div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No assignments generated yet</div>
          ) : (
            <div className="space-y-2">
              <table className="w-full text-sm">
                <thead className="bg-gray-700/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-gray-400">Task #</th>
                    <th className="px-3 py-2 text-left text-gray-400">Due Date</th>
                    <th className="px-3 py-2 text-left text-gray-400">Status</th>
                    <th className="px-3 py-2 text-left text-gray-400">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map(ass => (
                    <tr key={ass.id} className="border-b border-gray-700">
                      <td className="px-3 py-2 text-white font-mono text-xs">{ass.task_number}</td>
                      <td className="px-3 py-2 text-gray-300">{ass.due_date}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          ass.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          ass.status === 'overdue' ? 'bg-red-500/20 text-red-400' :
                          ass.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {ass.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-400">
                        {ass.completed_at ? new Date(ass.completed_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end">
          <button
            onClick={async () => {
              try {
                await api.post(`/tasks/recurring/${task.id}/generate`); // ✅ CSRF actif
                alert('New assignments generated');
                const response = await api.get(`/tasks/recurring/${task.id}`);
                setAssignments(response.data.assignments || []);
              } catch (error) {
                alert('Error generating assignments');
              }
            }}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm flex items-center gap-1"
          >
            <RefreshCw size={12} /> Generate Now
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const RecurringTasksView = () => {
  const [tasks, setTasks] = useState([]);
  const [zones, setZones] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [frequencyFilter, setFrequencyFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deletingTask, setDeletingTask] = useState(null);
  const [viewingAssignments, setViewingAssignments] = useState(null);
  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const [stats, setStats] = useState({
    activeTasks: 0,
    totalAssignments: 0,
    pendingAssignments: 0,
    overdueAssignments: 0
  });

  const fetchCompanySettings = useCallback(async () => {
    try {
      const response = await api.get('/company/settings'); // ✅ CSRF actif
      if (response.data) {
        setCompany(response.data);
        if (response.data.logo_url) setLogoUrl(`${API_BASE}${response.data.logo_url}`);
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tasksRes, zonesRes, templatesRes, staffRes] = await Promise.all([
        api.get('/tasks/recurring/'),       // ✅
        api.get('/airport/zones/'),         // ✅
        api.get('/inspections/templates'),  // ✅
        api.get('/staff/')                  // ✅
      ]);
      
      setTasks(tasksRes.data || []);
      setZones(zonesRes.data || []);
      setTemplates(templatesRes.data || []);
      setStaff(staffRes.data || []);
      
      const active = (tasksRes.data || []).filter(t => t.is_active).length;
      setStats({
        activeTasks: active,
        totalAssignments: (tasksRes.data || []).reduce((sum, t) => sum + (t.assignments_count || 0), 0),
        pendingAssignments: 0,
        overdueAssignments: 0
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, []);

  useEffect(() => {
    fetchCompanySettings();
    fetchData();
  }, [fetchData, fetchCompanySettings]);

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = 
      task.task_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.task_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && task.is_active) ||
      (statusFilter === 'inactive' && !task.is_active);
    const matchesFrequency = frequencyFilter === 'all' || task.frequency === frequencyFilter;
    return matchesSearch && matchesStatus && matchesFrequency;
  });

  const handleSaveTask = async (formData) => {
    try {
      if (editingTask) {
        await api.put(`/tasks/recurring/${editingTask.id}`, formData); // ✅ CSRF actif
      } else {
        await api.post('/tasks/recurring/', formData); // ✅ CSRF actif
      }
      await fetchData();
      setShowForm(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Error saving task:', error);
      alert(error.response?.data?.detail || 'Error saving task');
    }
  };

  const handleDeleteTask = async (reason) => {
    try {
      await api.delete(`/tasks/recurring/${deletingTask.id}`, { data: { reason } }); // ✅ CSRF actif
      await fetchData();
      setDeletingTask(null);
    } catch (error) {
      console.error('Error deleting task:', error);
      alert(error.response?.data?.detail || 'Error deleting task');
    }
  };

  const exportToExcel = () => {
    const exportData = filteredTasks.map(task => ({
      'Task #': task.task_number,
      'Title': task.task_title,
      'Frequency': task.frequency,
      'Start Date': task.start_date,
      'End Date': task.end_date || 'Ongoing',
      'Status': task.is_active ? 'Active' : 'Inactive',
      'Assigned To': task.assigned_to_name || task.assigned_role || 'Unassigned',
      'Escalation Hours': task.escalation_hours
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Recurring Tasks');
    XLSX.writeFile(wb, `recurring_tasks_${new Date().toISOString().slice(0,19)}.xlsx`);
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const companyName = company?.name || 'AeroTrick FBO';
    
    if (logoUrl) {
      try {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = logoUrl;
        await new Promise((resolve) => {
          img.onload = () => { doc.addImage(img, 'PNG', 14, 10, 40, 15); resolve(); };
          img.onerror = resolve;
        });
      } catch (e) {}
    }
    
    doc.setFontSize(18);
    doc.setTextColor(30, 64, 175);
    doc.text('Recurring Tasks Report', 70, 20);
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Active Tasks: ${stats.activeTasks}`, 14, 45);
    
    const tableData = filteredTasks.map(task => [
      task.task_number,
      task.task_title?.slice(0, 30) || '',
      task.frequency,
      task.start_date,
      task.is_active ? 'Active' : 'Inactive'
    ]);
    
    doc.autoTable({
      startY: 55,
      head: [['Task #', 'Title', 'Frequency', 'Start Date', 'Status']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
      margin: { left: 14, right: 14 }
    });
    
    const finalY = doc.lastAutoTable?.finalY || 250;
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.text(
      `Generated by AeroTrick for ${companyName} · © 2026 AeroTrick. All rights reserved.`,
      14, finalY + 20
    );
    
    doc.save(`recurring_tasks_${new Date().toISOString().slice(0,19)}.pdf`);
  };

  return (
    <div className="space-y-4 px-1">
      
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {logoUrl && (
            <img src={logoUrl} alt="Logo" className="h-10 w-auto object-contain"
              onError={(e) => e.target.style.display = 'none'} />
          )}
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <div className="p-1.5 bg-blue-500/20 rounded-lg">
                <Repeat size={18} className="text-blue-400" />
              </div>
              Recurring Tasks Management
            </h2>
            <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
              <Clock size={11} />
              Last refresh: {lastRefresh.toLocaleTimeString()} — Auto-generate scheduled tasks
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportToExcel} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 rounded-lg text-green-400 text-xs">
            <FileSpreadsheet size={12} /> Excel
          </button>
          <button onClick={exportToPDF} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg text-red-400 text-xs">
            <FileText size={12} /> PDF
          </button>
          <button onClick={fetchData} className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard title="Active Recurring Tasks" value={stats.activeTasks} icon={Repeat} color="#38bdf8" />
        <StatCard title="Total Assignments" value={stats.totalAssignments} icon={Calendar} color="#10b981" />
        <StatCard title="Pending" value={stats.pendingAssignments} icon={Clock} color="#f59e0b" />
        <StatCard title="Overdue" value={stats.overdueAssignments} icon={AlertCircle} color="#ef4444" />
      </div>
      
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search by title or task #..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-9 pr-4 text-white text-sm"
          />
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        
        <select
          value={frequencyFilter}
          onChange={(e) => setFrequencyFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm"
        >
          <option value="all">All Frequencies</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
        
        <button
          onClick={() => { setEditingTask(null); setShowForm(true); }}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm"
        >
          <Plus size={14} /> New Recurring Task
        </button>
      </div>
      
      {/* Tasks Table */}
      <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Task #</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Title</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Frequency</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Schedule</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Assigned To</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Status</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-12 text-gray-500"><Loader className="animate-spin mx-auto" /></td></tr>
              ) : filteredTasks.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-12 text-gray-500">No recurring tasks found</td></tr>
              ) : (
                filteredTasks.map((task) => (
                  <tr key={task.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-white font-mono text-sm">{task.task_number}</td>
                    <td className="px-4 py-3 text-gray-300 text-sm">{task.task_title}</td>
                    <td className="px-4 py-3"><FrequencyBadge frequency={task.frequency} /></td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      From: {task.start_date}<br />
                      {task.end_date && `To: ${task.end_date}`}
                    </td>
                    <td className="px-4 py-3 text-gray-300 text-sm">
                      {task.assigned_to_name || task.assigned_role || '—'}
                    </td>
                    <td className="px-4 py-3"><StatusBadge isActive={task.is_active} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setViewingAssignments(task)}
                          className="p-1 hover:bg-gray-600 rounded text-blue-400" title="View assignments">
                          <Eye size={14} />
                        </button>
                        <button onClick={() => { setEditingTask(task); setShowForm(true); }}
                          className="p-1 hover:bg-gray-600 rounded text-yellow-400" title="Edit">
                          <Edit2 size={14} />
                        </button>
                        <button onClick={() => setDeletingTask(task)}
                          className="p-1 hover:bg-gray-600 rounded text-red-400" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Footer */}
      <div className="text-center py-4">
        <p className="text-gray-500 text-xs">
          Generated by AeroTrick for {company?.name || 'AeroTrick FBO'} · © 2026 AeroTrick. All rights reserved.
        </p>
      </div>
      
      {/* Modals */}
      {showForm && (
        <TaskFormModal
          task={editingTask}
          onSave={handleSaveTask}
          onClose={() => { setShowForm(false); setEditingTask(null); }}
          zones={zones}
          templates={templates}
          staff={staff}
        />
      )}
      
      {deletingTask && (
        <DeleteModal
          task={deletingTask}
          onConfirm={handleDeleteTask}
          onClose={() => setDeletingTask(null)}
        />
      )}
      
      {viewingAssignments && (
        <AssignmentsModal
          task={viewingAssignments}
          onClose={() => setViewingAssignments(null)}
        />
      )}
      
    </div>
  );
};

export default RecurringTasksView;